// Next Imports
import { useParams } from 'next/navigation'

// MUI Imports
import { useTheme } from '@mui/material/styles'

// Type Imports
import type { getDictionary } from '@/utils/getDictionary'
import type { VerticalMenuContextProps } from '@menu/components/vertical-menu/Menu'

// Component Imports
import HorizontalNav, { Menu, SubMenu, MenuItem } from '@menu/horizontal-menu'
import VerticalNavContent from './VerticalNavContent'

// import { GenerateHorizontalMenu } from '@components/GenerateMenu'

// Hook Imports
import useVerticalNav from '@menu/hooks/useVerticalNav'
import { useSettings } from '@core/hooks/useSettings'

// Styled Component Imports
import StyledHorizontalNavExpandIcon from '@menu/styles/horizontal/StyledHorizontalNavExpandIcon'
import StyledVerticalNavExpandIcon from '@menu/styles/vertical/StyledVerticalNavExpandIcon'

// Style Imports
import menuRootStyles from '@core/styles/horizontal/menuRootStyles'
import menuItemStyles from '@core/styles/horizontal/menuItemStyles'
import verticalNavigationCustomStyles from '@core/styles/vertical/navigationCustomStyles'
import verticalMenuItemStyles from '@core/styles/vertical/menuItemStyles'
import verticalMenuSectionStyles from '@core/styles/vertical/menuSectionStyles'

// Menu Data Imports
// import menuData from '@/data/navigation/horizontalMenuData'

type RenderExpandIconProps = {
  level?: number
}

type RenderVerticalExpandIconProps = {
  open?: boolean
  transitionDuration?: VerticalMenuContextProps['transitionDuration']
}

const RenderExpandIcon = ({ level }: RenderExpandIconProps) => (
  <StyledHorizontalNavExpandIcon level={level}>
    <i className='ri-arrow-right-s-line' />
  </StyledHorizontalNavExpandIcon>
)

const RenderVerticalExpandIcon = ({ open, transitionDuration }: RenderVerticalExpandIconProps) => (
  <StyledVerticalNavExpandIcon open={open} transitionDuration={transitionDuration}>
    <i className='ri-arrow-right-s-line' />
  </StyledVerticalNavExpandIcon>
)

const HorizontalMenu = ({ dictionary }: { dictionary: Awaited<ReturnType<typeof getDictionary>> }) => {
  // Hooks
  const verticalNavOptions = useVerticalNav()
  const theme = useTheme()
  const { settings } = useSettings()
  const params = useParams()

  // Vars
  const { skin } = settings
  const { transitionDuration } = verticalNavOptions
  const { lang: locale } = params

  return (
    <HorizontalNav
      switchToVertical
      verticalNavContent={VerticalNavContent}
      verticalNavProps={{
        customStyles: verticalNavigationCustomStyles(verticalNavOptions, theme),
        backgroundColor:
          skin === 'bordered' ? 'var(--mui-palette-background-paper)' : 'var(--mui-palette-background-default)'
      }}
    >
      <Menu
        rootStyles={menuRootStyles(theme)}
        renderExpandIcon={({ level }) => <RenderExpandIcon level={level} />}
        renderExpandedMenuItemIcon={{ icon: <i className='ri-circle-line' /> }}
        menuItemStyles={menuItemStyles(theme, 'ri-circle-line')}
        popoutMenuOffset={{
          mainAxis: ({ level }) => (level && level > 0 ? 4 : 16),
          alignmentAxis: 0
        }}
        verticalMenuProps={{
          menuItemStyles: verticalMenuItemStyles(verticalNavOptions, theme),
          renderExpandIcon: ({ open }) => (
            <RenderVerticalExpandIcon open={open} transitionDuration={transitionDuration} />
          ),
          renderExpandedMenuItemIcon: { icon: <i className='ri-circle-line' /> },
          menuSectionStyles: verticalMenuSectionStyles(verticalNavOptions, theme)
        }}
      >
        <SubMenu label={dictionary['navigation'].vectax} icon={<i className='ri-instance-line' />}>
          <MenuItem href={`/${locale}/vectax/overview`}>{dictionary['navigation'].overview}</MenuItem>
          <MenuItem href={`/${locale}/vectax/myPlan`}>{dictionary['navigation'].myplan}</MenuItem>
          <MenuItem href={`/${locale}/vectax/usage`}>{dictionary['navigation'].vectaxusage}</MenuItem>
          <MenuItem href={`/${locale}/vectax/playground`}>{dictionary['navigation'].playground}</MenuItem>
        </SubMenu>
        <SubMenu label={dictionary['navigation'].agentIq} icon={<i className='ri-shield-line' />}>
          <MenuItem href={`/${locale}/agentIq/overview`}>{dictionary['navigation'].overview}</MenuItem>
          <MenuItem href={`/${locale}/agentIq/playground`}>{dictionary['navigation'].playground}</MenuItem>
          <MenuItem href={`/${locale}/dashboards/myPlan`}>{dictionary['navigation'].myplan}</MenuItem>
        </SubMenu>
        <SubMenu label={dictionary['navigation'].policyiq} icon={<i className='ri-folder-shield-2-line' />}>
          <MenuItem href={`/${locale}/policyiq/overview`} disabled>
            {dictionary['navigation'].overview}
          </MenuItem>
          <MenuItem href={`/${locale}/policyiq/playground`} disabled>
            {dictionary['navigation'].playground}
          </MenuItem>
          <MenuItem href={`/${locale}/policyiq/semanticSearch`} disabled>
            {dictionary['navigation'].semanticsearch}
          </MenuItem>
          <MenuItem href={`/${locale}/policyiq/myPlan`} disabled>
            {dictionary['navigation'].myplan}
          </MenuItem>
        </SubMenu>
        <SubMenu label={dictionary['navigation'].discover} icon={<i className='ri-home-smile-line' />}>
          <MenuItem href={`/${locale}/discover/overview`}>{dictionary['navigation'].overview}</MenuItem>
          <MenuItem href={`/${locale}/discover/playground`}>{dictionary['navigation'].playground}</MenuItem>
          <MenuItem href={`/${locale}/discover/redTeaming`}>{dictionary['navigation'].redteaming}</MenuItem>
          <MenuItem href={`/${locale}/discover/myPlan`}>{dictionary['navigation'].myplan}</MenuItem>
        </SubMenu>
        <MenuItem href={`/${locale}/apikeys/keys`} icon={<i className='ri-key-2-line' />}>
          {dictionary['navigation'].apiKeys}
        </MenuItem>
        {/*Teams as a single menu item */}
        <MenuItem icon={<i className='ri-team-line' />} href={`/${locale}/teams`}>
          {dictionary['navigation'].team}
        </MenuItem>
        <MenuItem href={`/${locale}/pages/account-settings`} icon={<i className='ri-user-settings-line' />}>
          {dictionary['navigation'].settings}
        </MenuItem>
      </Menu>

      {/* <Menu
        rootStyles={menuRootStyles(theme)}
        renderExpandIcon={({ level }) => <RenderExpandIcon level={level} />}
        renderExpandedMenuItemIcon={{ icon: <i className='ri-circle-line' /> }}
        menuItemStyles={menuItemStyles(theme, 'ri-circle-line')}
        popoutMenuOffset={{
          mainAxis: ({ level }) => (level && level > 0 ? 4 : 16),
          alignmentAxis: 0
        }}
        verticalMenuProps={{
          menuItemStyles: verticalMenuItemStyles(verticalNavOptions, theme),
          renderExpandIcon: ({ open }) => (
            <RenderVerticalExpandIcon open={open} transitionDuration={transitionDuration} />
          ),
          renderExpandedMenuItemIcon: { icon: <i className='ri-circle-line' /> },
          menuSectionStyles: verticalMenuSectionStyles(verticalNavOptions, theme)
        }}
      >
        <GenerateHorizontalMenu menuData={menuData(dictionary)} />
      </Menu> */}
    </HorizontalNav>
  )
}

export default HorizontalMenu
